import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import 'moment/dist/locale/es';
import CardBrand from 'src/pages/Settings/components/SubscriptionsManager/components/CardSelector/CardBrand.component';
import { tPaymentMethod } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';

export type tTrialCardPickerProps = {
  /** The saved cards, already read. One card shows no radio. */
  paymentMethods: tPaymentMethod[];
  /** Id of the chosen card, or null while nothing is chosen. */
  value: string | null;
  onChange: (paymentMethodId: string) => void;
  /** The card that paid the trial, or null when billing did not say. */
  trialChargePaymentMethodId: string | null;
  /** Opens the add-payment-method form. */
  onAddCard: () => void;
};

type tCardRowProps = {
  paymentMethod: tPaymentMethod;
  trialChargePaymentMethodId: string | null;
};

// Dates on the billing surfaces are Spanish, like the rest of this modal.
const formatAddedOn = (createdAt: string): string =>
  moment(createdAt).locale('es').format('D [de] MMMM');

function CardRowContent(props: tCardRowProps): ReactElement {
  const { t } = useTranslation('subscription/modals');
  const { paymentMethod } = props;

  // RET-2428: the trial card says so; another card says when it was added;
  // a card without a date shows no detail line.
  let detail: string | null = null;
  if (paymentMethod.paymentMethodId === props.trialChargePaymentMethodId) {
    detail = t('trialConfirmation.cardPicker.paidTrial');
  } else if (paymentMethod.createdAt) {
    detail = t('trialConfirmation.cardPicker.addedOn', {
      date: formatAddedOn(paymentMethod.createdAt),
    });
  }

  return (
    <>
      <span aria-hidden="true" className="flex shrink-0 items-center">
        <CardBrand brand={paymentMethod.brand} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-base font-medium text-[var(--ls-text-neutral-default)]">
          {t('trialConfirmation.cardPicker.cardEndingIn', {
            lastDigits: paymentMethod.lastDigits,
          })}
        </span>
        {detail !== null && (
          <span className="text-[0.8125rem] text-[var(--ls-text-neutral-light)]">
            {detail}
          </span>
        )}
      </span>
      {paymentMethod.month !== null && paymentMethod.year !== null && (
        <span className="shrink-0 text-sm text-[var(--ls-text-neutral-light)]">
          {t('trialConfirmation.cardPicker.expires', {
            month: String(paymentMethod.month).padStart(2, '0'),
            year: String(paymentMethod.year % 100).padStart(2, '0'),
          })}
        </span>
      )}
    </>
  );
}

type tAddCardLinkProps = { label: string; onClick: () => void };

function AddCardLink(props: tAddCardLinkProps): ReactElement {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="flex min-h-[var(--ls-tap-min)] items-center gap-1.5 self-start text-sm font-medium text-[var(--ls-text-primary-default)] hover:text-[var(--ls-text-primary-hover)] focus-visible:outline focus-visible:outline-[length:var(--ls-focus-ring-width)] focus-visible:outline-offset-[var(--ls-focus-ring-offset)] focus-visible:outline-[color:var(--ls-focus-ring-color)]"
    >
      <PlusIcon aria-hidden="true" className="h-[1.125rem] w-[1.125rem]" />
      {props.label}
    </button>
  );
}

const ROW_CLASSES =
  'flex min-h-16 items-center gap-3 px-4 py-3 text-left';

/**
 * RET-2428 (rule T-2, case D-27): the card the trial confirmation charges the
 * plan to, as the approved mockup draws it. Two or more cards are one radio
 * group with the helper text and «Agregar otro método de pago»; one card is
 * a plain row with «Usar otra tarjeta». The caller owns the selection.
 *
 * The DS radio has no React export and its CSS is not loaded in the front,
 * so the options are native radios composed with Tailwind and `--ls-*`
 * tokens (engineering-contract, UI rule 2). Rows sit in one bordered
 * container separated by dividers (composition, Surfaces and depth). Each row
 * is the whole label, at least `--ls-tap-min` tall.
 */
export default function TrialCardPicker(
  props: tTrialCardPickerProps,
): ReactElement | null {
  const { t } = useTranslation('subscription/modals');

  if (props.paymentMethods.length === 0) return null;

  if (props.paymentMethods.length === 1) {
    const [loneCard] = props.paymentMethods;
    return (
      <div className="flex flex-col gap-2 text-left">
        <h4 className="text-base font-bold text-[var(--ls-text-neutral-default)]">
          {t('trialConfirmation.cardPicker.title')}
        </h4>
        <div
          data-testid={`trial-card-row-${loneCard.paymentMethodId}`}
          className={`${ROW_CLASSES} rounded-[var(--ls-radius-md)] border border-[var(--ls-border-neutral-light)]`}
        >
          <CardRowContent
            paymentMethod={loneCard}
            trialChargePaymentMethodId={props.trialChargePaymentMethodId}
          />
        </div>
        <AddCardLink
          label={t('trialConfirmation.cardPicker.useAnother')}
          onClick={props.onAddCard}
        />
      </div>
    );
  }

  return (
    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0 text-left">
      <legend className="mb-2 p-0 text-base font-bold text-[var(--ls-text-neutral-default)]">
        {t('trialConfirmation.cardPicker.title')}
      </legend>
      <p className="mb-1 text-sm text-[var(--ls-text-neutral-light)]">
        {t('trialConfirmation.cardPicker.helper')}
      </p>
      <div className="flex flex-col divide-y divide-[var(--ls-border-neutral-light)] overflow-hidden rounded-[var(--ls-radius-md)] border border-[var(--ls-border-neutral-light)]">
        {props.paymentMethods.map((paymentMethod) => {
          const isChosen = props.value === paymentMethod.paymentMethodId;
          return (
            <label
              key={paymentMethod.paymentMethodId}
              data-testid={`trial-card-option-${paymentMethod.paymentMethodId}`}
              className={`${ROW_CLASSES} cursor-pointer ${
                isChosen
                  ? 'bg-[var(--ls-accent-primary-lighter)]'
                  : '[@media(hover:hover)]:hover:bg-[var(--ls-state-neutral-hover)]'
              }`}
            >
              <input
                type="radio"
                name="trial-confirmation-card"
                value={paymentMethod.paymentMethodId}
                checked={isChosen}
                onChange={() => props.onChange(paymentMethod.paymentMethodId)}
                className="m-0 h-4 w-4 shrink-0 cursor-pointer accent-[var(--ls-accent-primary-default)] focus-visible:outline focus-visible:outline-[length:var(--ls-focus-ring-width)] focus-visible:outline-offset-[var(--ls-focus-ring-offset)] focus-visible:outline-[color:var(--ls-focus-ring-color)]"
              />
              <CardRowContent
                paymentMethod={paymentMethod}
                trialChargePaymentMethodId={props.trialChargePaymentMethodId}
              />
            </label>
          );
        })}
      </div>
      <AddCardLink
        label={t('trialConfirmation.cardPicker.addAnother')}
        onClick={props.onAddCard}
      />
    </fieldset>
  );
}
